import { PASSWORD_CHANGE_REQUIRED, notifyPasswordChangeRequired } from './passwordChangeSignal';

const getSessionHeaders = () => {
  const sessionId = localStorage.getItem('reacta_sessionId')
    || sessionStorage.getItem('reacta_sessionId')
    || localStorage.getItem('reacta_session_id')
    || sessionStorage.getItem('reacta_session_id');
  return sessionId ? { 'x-session-id': sessionId } : {};
};

const buildOptions = (method, body, extraHeaders = {}) => ({
  method,
  headers: {
    'Content-Type': 'application/json',
    ...getSessionHeaders(),
    ...extraHeaders,
  },
  body: body ?JSON.stringify(body) : undefined,
});

const getRuntimeApiBase = () => {
  const envBase = import.meta.env.VITE_API_BASE;
  const windowBase = typeof window !== 'undefined' ? window.__API_BASE__ : '';

  if (envBase) {
    return envBase.replace(/\/$/, '');
  }

  if (windowBase) {
    return String(windowBase).replace(/\/$/, '');
  }

  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    const isLocalFrontend = ['localhost', '127.0.0.1'].includes(hostname) && ['3000', '5173'].includes(port);
    if (isLocalFrontend) {
      return 'http://127.0.0.1:3001';
    }
  }

  return '';
};

const resolveEndpoint = (endpoint) => {
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }

  const apiBase = getRuntimeApiBase();
  return `${apiBase}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
};

// Sprint B: o status HTTP era descartado ao virar Error — todo chamador
// (ex.: StaticAiChat.jsx) so via a mensagem de texto, sem como distinguir
// um 403 de autoridade (Secao 30: ACCESS DENIED) de um 500/network error
// generico. buildApiError anexa .status e .code (errorJson.error) ao
// Error lancado, unico ponto de correcao para toda chamada via apiClient.
const buildApiError = (message, status, code, payload) => {
  const err = new Error(message);
  err.status = status;
  err.code = code;
  err.payload = payload;
  return err;
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const errorText = await response.text();
    if (!contentType.includes('application/json')) {
      const isHtml = errorText.trim().startsWith('<!DOCTYPE') || errorText.trim().startsWith('<html');
      throw buildApiError(isHtml
        ? 'Backend ORION nao respondeu JSON. Inicie o server.js ou verifique o proxy /api.'
        : errorText || `HTTP ${response.status}`, response.status);
    }
    try {
      const errorJson = JSON.parse(errorText);
      if (response.status === 403 && errorJson?.error === PASSWORD_CHANGE_REQUIRED) notifyPasswordChangeRequired();
      throw buildApiError(errorJson.details || errorJson.error || `HTTP ${response.status}`, response.status, errorJson.error, errorJson);
    } catch (parseError) {
      if (parseError.message && parseError.message !== 'Unexpected end of JSON input') {
        throw parseError;
      }
      throw buildApiError(errorText || `HTTP ${response.status}`, response.status);
    }
  }
  if (response.status === 204) {
    return null;
  }
  if (!contentType.includes('application/json')) {
    const responseText = await response.text();
    const isHtml = responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html');
    throw new Error(isHtml
      ? 'Backend ORION nao respondeu JSON. Inicie o server.js ou verifique o proxy /api.'
      : 'Resposta nao JSON recebida do backend ORION.');
  }
  return response.json();
};

export const apiClient = {
  get: async (endpoint, options = {}) => {
    const response = await fetch(resolveEndpoint(endpoint), {
      method: 'GET',
      ...options,
      headers: {
        ...getSessionHeaders(),
        ...(options.headers || {}),
      },
    });
    return handleResponse(response);
  },
  post: async (endpoint, body, options = {}) => {
    const response = await fetch(resolveEndpoint(endpoint), buildOptions('POST', body, options.headers));
    return handleResponse(response);
  },
  put: async (endpoint, body, options = {}) => {
    const response = await fetch(resolveEndpoint(endpoint), buildOptions('PUT', body, options.headers));
    return handleResponse(response);
  },
  patch: async (endpoint, body, options = {}) => {
    const response = await fetch(resolveEndpoint(endpoint), buildOptions('PATCH', body, options.headers));
    return handleResponse(response);
  },
  delete: async (endpoint, options = {}) => {
    const response = await fetch(resolveEndpoint(endpoint), {
      method: 'DELETE',
      ...options,
      headers: {
        ...getSessionHeaders(),
        ...(options.headers || {}),
      },
    });
    return handleResponse(response);
  },
};
